import {API_URL} from "@/shared/lib/api";
import {AutoTableInterface} from "@/features/types/AutoTableInterface";
import {PresetStorage, PresetValue} from "@/features/types/AutoTablePresetInterface";
import {Filters} from "@/features/types/AutoTableFilterInterface";

export interface SessionStatus {
    isLogged: boolean;
    hasUserName: boolean;
    hasDefaultFilter: boolean;
    sessionReady: boolean;
    userName?: string;
    defaultFilter?: string;
    message?: string;
}

/**
 * Sprawdza czy sesja PHP jest zainicjowana
 * Zwraca status sesji - czy użytkownik jest zalogowany i czy wszystkie wymagane zmienne są ustawione
 */
export const checkSession = async (): Promise<SessionStatus> => {
    const res = await fetch(`${API_URL}/index.php?action=check-session`, {
        method: "GET",
        headers: {"Content-Type": "application/json"},
        credentials: "include",
    });

    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    return await res.json();
};

/**
 * Ustawia sesję deweloperską (tylko w środowisku deweloperskim)
 * Pozwala na testowanie bez logowania do głównego projektu PHP
 */
export const setDevSession = async (userName: string, defaultFilter: string, root: string = 'SN'): Promise<{success: boolean; message: string}> => {
    const res = await fetch(`${API_URL}/index.php?action=set-dev-session`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        credentials: "include",
        body: JSON.stringify({userName, defaultFilter, root}),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || `HTTP error! Status: ${res.status}`);
    }
    
    return await res.json();
};

export const getUserName = async (): Promise<string> => {
    const res = await fetch(`${API_URL}/index.php?action=get-user-name`, {
        method: "GET",
        headers: {"Content-Type": "application/json"},
        credentials: "include",
    });

    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    return await res.json();
};

/**
 * Pobiera rolę użytkownika z tabeli CommEmployeeTable
 * Zwraca f_emp_c_role (np. "MAN" dla managera) lub null jeśli użytkownik nie ma roli
 */
export const getUserRole = async (): Promise<string | null> => {
    const res = await fetch(`${API_URL}/index.php?action=get-user-role`, {
        method: "GET",
        headers: {"Content-Type": "application/json"},
        credentials: "include",
    });

    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    return await res.json();
};

export async function getAutoTableData(filters: Filters, rowLimit?: number): Promise<AutoTableInterface[]> {
    const limit = rowLimit ?? 1000;
    const res = await fetch(`${API_URL}/index.php?action=auto-table`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include',
        body: JSON.stringify({filters, limit}),
    });

    // console.log(filters)
    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    return await res.json();
}

export async function getAutoTableRowById(f_id: string): Promise<AutoTableInterface | null> {
    const res = await fetch(`${API_URL}/index.php?action=auto-table-row`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        credentials: "include",
        body: JSON.stringify({f_id}),
    });

    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    return await res.json();
}

export async function getAutoTableOptions(column: keyof AutoTableInterface, filters?: Filters, isSearch?: boolean): Promise<string[]> {
    const res = await fetch(`${API_URL}/index.php?action=auto-table-options`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include',
        body: JSON.stringify({column, filters: filters ?? {}, isSearch: isSearch ?? false}),
    });

    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    const data = await res.json();
    return data[column] || [];
}

export const getAutoTableOptionsWithFilters = (column: keyof AutoTableInterface) => {
    return async (filters: Filters | Record<string, string>, isSearch?: boolean) => {
        const options = await getAutoTableOptions(column, filters as Filters, isSearch);
        return options.map((o) => ({label: o, value: o}));
    };
};

export const getAutoEquTableDetails = async (f_ord_num: string, f_ord_d: string) => {
    const res = await fetch(`${API_URL}/index.php?action=auto-table-equ-details`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        credentials: "include",
        body: JSON.stringify({f_ord_num, f_ord_d}),
    });

    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    return await res.json();
};

export const getAllAutoTablePresetFilters = async (): Promise<PresetStorage> => {
    const res = await fetch(`${API_URL}/index.php?action=auto-table-preset-get`, {
        method: "GET",
        headers: {"Content-Type": "application/json"},
        credentials: "include",
    });

    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    return await res.json();
};

export const saveAutoTablePresetFilter = async (preset_name: string, preset: PresetValue) => {
    const res = await fetch(`${API_URL}/index.php?action=auto-table-preset-save`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        credentials: "include",
        body: JSON.stringify({preset_name, preset}),
    });

    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    return await res.json();
};

export const setDefaultAutoTablePresetFilter = async (preset_name?: string) => {
    const res = await fetch(`${API_URL}/index.php?action=auto-table-preset-default`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        credentials: "include",
        body: JSON.stringify({preset_name}),
    });

    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    return await res.json();
};

export const deleteAutoTablePresetFilter = async (preset_name: string) => {
    const res = await fetch(`${API_URL}/index.php?action=auto-table-preset-delete`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        credentials: "include",
        body: JSON.stringify({preset_name}),
    });

    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    return await res.json();
};

/**
 * Zmienia aktywną bazę danych w sesji PHP
 * @param database - Nazwa bazy danych (np. 'BEMO_COMM', 'ACLUB_COMM', etc.)
 */
export const changeDatabase = async (database: string): Promise<{success: boolean; message: string; defaultFilter: string}> => {
    const res = await fetch(`${API_URL}/index.php?action=change-database`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        credentials: "include",
        body: JSON.stringify({database}),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || `HTTP error! Status: ${res.status}`);
    }
    
    return await res.json();
};